import { DateTime } from '@cheqroom/date-time';

import { MAX_QUERYSTRING_LENGTH } from '../core/constants';
import {
	CUSTOM_FIELD_WILDCARD_STUB_PATHS,
	expandCustomFieldPaths,
	projectionIncludesCustomFieldsBag,
} from '../core/custom-field-paths';
import { HttpErrorLike, mapHttpErrorToResourceError } from '../core/errors';
import { DocumentType, PythonSearchVariables, ResourceSortState, TableQueryFilters } from '../core/types';

/** Shared helpers for resource hooks. */

/** fromBetween: DateTime → startOfDay UTC ISO; string unchanged. */
export function serializeFromBetweenForSearch(value: DateTime | string | undefined): string | undefined {
	if (value == null) return undefined;
	if (typeof value === 'string') return value;
	return value.startOfDay().toFloatingISOString();
}

/** toBetween: DateTime → endOfDay UTC ISO; string unchanged. */
export function serializeToBetweenForSearch(value: DateTime | string | undefined): string | undefined {
	if (value == null) return undefined;
	if (typeof value === 'string') return value;
	return value.endOfDay().toFloatingISOString();
}

/** ResourceSortState → Python _sort string (e.g. 'name,-created'). Optional sortFieldMapper for field id mapping. */
export const convertSortToPython = (sort: ResourceSortState, sortFieldMapper?: (fieldId: string) => string): string => {
	if (!sort || sort.length === 0) {
		return '';
	}

	return sort
		.map((sortItem) => {
			const fieldId = sortFieldMapper ? sortFieldMapper(sortItem.id) : sortItem.id;
			return sortItem.desc ? `-${fieldId}` : fieldId;
		})
		.join(',');
};

/**
 * Build common search params from filters and fields (list, search, sort, customFieldFilters, _fields).
 * Leaves _limit/_skip to caller. Prefer resource-specific builders when filters have extra keys.
 */
export const buildCommonSearchVariables = (
	filters: Partial<TableQueryFilters> | undefined,
	fields: readonly string[],
	defaultFields: readonly string[] | undefined,
	defaultSearchParams: Record<string, unknown> | undefined,
	{
		replaceFields = false,
		fieldKinds,
		expandCustomFields = true,
	}: {
		replaceFields?: boolean;
		fieldKinds?: Readonly<Record<string, string>>;
		expandCustomFields?: boolean;
	} = {}
): Partial<PythonSearchVariables> => {
	const searchVariables: Partial<PythonSearchVariables> = {
		...defaultSearchParams,
	};

	if (filters?.list) {
		searchVariables.listName = filters.list;
	}

	if (filters?.search) {
		searchVariables.query = filters.search;
	}

	if (filters?.sort && filters.sort.length > 0) {
		const sortString = convertSortToPython(filters.sort);
		if (sortString) {
			searchVariables._sort = sortString;
		}
	}

	const fieldsString = buildResourceFieldsString(fields, defaultFields ?? [], {
		replaceFields,
		fieldKinds,
		expandCustomFields,
	});
	// Omit `_fields` when the projection is the default wildcard (API returns everything).
	if (fieldsString && fieldsString !== '*') {
		searchVariables._fields = fieldsString;
	}

	if (filters?.customFieldFilters) {
		Object.assign(searchVariables, filters.customFieldFilters);
	}

	return searchVariables;
};

/**
 * Map caller column ids, expand custom field columns, merge defaults, dedupe, and join.
 * See `expandCustomFieldPaths` and `docs/layers/api-client.md`.
 */
export const buildResourceFieldsString = (
	callerFields: readonly string[],
	defaultFields: readonly string[],
	{
		replaceFields = false,
		mapFieldId,
		expandCustomFields = true,
		renameField,
		fieldKinds,
	}: {
		replaceFields?: boolean;
		/** Column id → API path(s). Return an array to expand one id into several. */
		mapFieldId?: (field: string) => string | readonly string[];
		/** Opt out only for a resource that has no custom fields. */
		expandCustomFields?: boolean;
		/** Applied after the merge, so it also rewrites defaults (e.g. items → itemThumbnails). */
		renameField?: (field: string) => string;
		/** Column id → custom field kind. Customer-kind paths expand to named stub sub-paths. */
		fieldKinds?: Readonly<Record<string, string>>;
	} = {}
): string => {
	const mapped = mapFieldId ? callerFields.flatMap((field) => mapFieldId(field)) : [...callerFields];
	const expanded = expandCustomFields ? expandCustomFieldPaths(mapped, { fieldKinds }) : mapped;
	const merged = replaceFields && expanded.length > 0 ? expanded : [...defaultFields, ...expanded];
	// No kind map (or empty) → append wildcard stubs so customer refs in the `fields` bag expand.
	const hasFieldKinds = fieldKinds != null && Object.keys(fieldKinds).length > 0;
	const withWildcardStubs =
		expandCustomFields && !hasFieldKinds && projectionIncludesCustomFieldsBag(merged)
			? [...merged, ...CUSTOM_FIELD_WILDCARD_STUB_PATHS]
			: merged;
	const renamed = renameField ? withWildcardStubs.map(renameField) : withWildcardStubs;
	return Array.from(new Set(renamed)).join(',');
};

/** Fields array → comma-separated string; falls back to defaultFields or '*'. */
export const buildFieldsString = (
	fields?: readonly string[],
	defaultFields?: readonly string[]
): string | undefined => {
	if (fields && fields.length > 0) {
		return fields.join(',');
	}
	if (defaultFields && defaultFields.length > 0) {
		return defaultFields.join(',');
	}
};

/** Add _fields to body when responseFields provided; otherwise return body unchanged. */
export const addResponseFields = <T extends Record<string, unknown>>(
	body: T,
	responseFields?: string[]
): T & { _fields?: string } => {
	if (responseFields && responseFields.length > 0) {
		return {
			...body,
			_fields: responseFields.join(','),
		};
	}
	return body;
};

/** Add _fields to body; use defaultField (default '_id') when responseFields empty. */
export const addResponseFieldsWithDefault = <T extends Record<string, unknown>>(
	body: T,
	responseFields?: string[],
	defaultField = '_id'
): T & { _fields: string } => {
	if (responseFields && responseFields.length > 0) {
		return {
			...body,
			_fields: responseFields.join(','),
		};
	}
	return {
		...body,
		_fields: defaultField,
	};
};

export { MAX_QUERYSTRING_LENGTH, DEFAULT_STALE_TIME } from '../core/constants';

/** True if url + params would exceed MAX_QUERYSTRING_LENGTH (use POST instead of GET). */
export const shouldUsePostForUrl = (url: string, params?: Record<string, unknown>): boolean => {
	if (!params || Object.keys(params).length === 0) {
		return false;
	}

	const queryString = Object.keys(params)
		.map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(String(params[k]))}`)
		.join('&');
	const fullUrl = `${url}?${queryString}`;

	return fullUrl.length >= MAX_QUERYSTRING_LENGTH;
};

/** Normalize resource API errors to typed ResourceApiError; rethrow others. */
export const handleResourceError = (error: unknown): never => {
	if (error instanceof Error && error.message === 'timeout') {
		const timeoutError = Object.assign(new Error('timeout'), {
			name: 'NetworkTimeout',
			status: 408,
		} as HttpErrorLike);
		throw mapHttpErrorToResourceError(timeoutError);
	}
	throw error;
};

export const getEndpointForDocumentType = (documentType: DocumentType) => {
	switch (documentType) {
		case 'checkouts':
			return 'orders';
		case 'users':
			return 'customers';
		default:
			return documentType;
	}
};
